// src/components/Admin/Common/TagManagementSection.tsx

import React from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Stack,
  Chip,
  Tooltip,
} from "@mui/material";
import { Add, Delete, LocalOffer } from "@mui/icons-material";
import TagEditorDialog from "../Editors/TagEditorDialog";
import { useTagManagement } from "../../../hooks/tag-management-hooks";
import { CacheKey } from "../../../types/cache-keys";
import { validId } from "../../../hooks/base-hooks";


type TagManagementSectionProps = {
  parentId: number | null;
  parentType: CacheKey;
  disabled?: boolean;
};

export default function TagManagementSection({
  parentId,
  parentType,
  disabled = false,
}: TagManagementSectionProps) {

  const {
    tagEditorOpen,
    setTagEditorOpen,
    editingTag,
    setEditingTag,
    selectedTag,
    setSelectedTag,
    attachedTagsByKey,
    availableTags,
    availableTagsByKey,
    handleCreateTag,
    handleEditOrViewTag,
    handleTagSaved,
    handleAttachTag,
    handleDetachTag,
    getParentChain,
    canManage,
    isLoading: tagsLoading,
  } = useTagManagement({
    parentId,
    parentType,
    enabled: validId(parentId),
  });

  if (!canManage) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Tags
          </Typography>
          <Button size="small" startIcon={<Add />} onClick={handleCreateTag} variant="outlined" disabled={true}>
            New Tag
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Save the record first to manage tags.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Tags
          </Typography>
          {!disabled && (
            <Button size="small" startIcon={<Add />} onClick={handleCreateTag} variant="outlined" disabled={tagsLoading}>
              New Tag
            </Button>
          )}
        </Box>

        {/* Attach Existing Tag */}
        {!disabled && (
          <Box display="flex" gap={1} mb={2}>
            <TextField
              select
              size="small"
              label="Attach existing tag"
              value={selectedTag?.id ?? ""}
              onChange={(e) => {
                const id = Number(e.target.value);
                setSelectedTag(availableTags.find((x: any) => x.id === id) || null);
              }}
              sx={{ flex: 1 }}
              disabled={tagsLoading || availableTags.length === 0}
            >
              {Object.entries(availableTagsByKey).map(([type, tags]) => [
                <MenuItem key={`header-${type}`} disabled>
                  <Typography variant="caption" fontWeight={600}>
                    {type}
                  </Typography>
                </MenuItem>,
                ...(tags as any[]).map((t: any) => {
                  const parentChain = getParentChain(t.id);
                  return (
                    <MenuItem key={t.id} value={t.id}>
                      <Box>
                        <Typography variant="body2">&nbsp;&nbsp;{t.key}:{t.value}</Typography>
                        {parentChain.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            &nbsp;&nbsp;{parentChain.join(" › ")}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  );
                }),
              ])}
              {availableTags.length === 0 && (
                <MenuItem disabled>
                  <Typography variant="caption">All tags attached</Typography>
                </MenuItem>
              )}
            </TextField>
            <Button variant="contained" disabled={!selectedTag || tagsLoading || availableTags.length === 0} onClick={handleAttachTag} size="small">
              Attach
            </Button>
          </Box>
        )}

        {/* Attached Tags Display */}
        <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: "auto" }}>
          {Object.entries(attachedTagsByKey).length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {tagsLoading ? "Loading tags..." : "No tags attached"}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {Object.entries(attachedTagsByKey).map(([key, tags]) => (
                <Box key={key}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    {key}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {(tags as any[]).map((tag: any) => {
                      const tooltipContent =
                        tag.parentChain.length > 0
                          ? `${tag.parentChain.join(" › ")} › ${tag.key + ':' + tag.value}`
                          : tag.key + ':' + tag.value;

                      return (
                        <Tooltip key={tag.id} title={tooltipContent}>
                          <Chip
                            label={tag.key + ':' + tag.value}
                            size="small"
                            icon={
                              tag.parentChain.length > 0 ? (
                                <LocalOffer fontSize="small" />
                              ) : undefined
                            }
                            {...(!disabled && {
                              onDelete: () => {
                                handleDetachTag(tag.joinId);
                              },
                              deleteIcon: <Delete fontSize="small" />
                            })}
                            onClick={() => handleEditOrViewTag(tag.id)}
                          />
                        </Tooltip>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      </Box>

      {/* Tag Editor Dialog */}
      <TagEditorDialog
        open={tagEditorOpen}
        tag={editingTag}
        onClose={() => {
          setTagEditorOpen(false);
          setEditingTag(null);
        }}
        onSaved={handleTagSaved}
        readOnly={disabled}
      />
    </>
  );
}